import {
    computed,
    inject,
    Injectable,
    linkedSignal,
    signal,
} from '@angular/core';
import {
    APP_CONFIG_TOKEN,
    isSupportedDocumentFileType,
} from '@quipex/core/config';
import {
    FILE_PREVIEW_ERROR,
    injectError,
    type BuildUrnParts,
} from '@quipex/core/http';
import {
    normalizeToProblemDetails,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import { extractFileExtension } from '@quipex/shared/utils';
import {
    FILE_PREVIEW_CONTENT_STATUS,
    FILE_PREVIEW_TYPE,
    type FilePreviewContentStatus,
    type FilePreviewType,
} from './models';

type PreviewStatus = 'loading' | 'ready' | 'error';

interface FilePreviewViewState {
    contentLoadStatus: FilePreviewContentStatus;
    errorDetails: ResolvedProblemDetails | null;
}

export interface FilePreviewStateInput {
    previewUrl: string | null | undefined;
    fileName: string;
}

@Injectable()
export class UiState {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly clientError = injectError();
    //#endregion

    //#region Constants
    private readonly loggingId: BuildUrnParts = ['service', 'file-preview'];
    //#endregion

    //#region State
    private readonly params = signal<FilePreviewStateInput>({
        previewUrl: undefined,
        fileName: '',
    });
    private readonly viewerState = linkedSignal({
        source: () => {
            const { previewUrl, fileName } = this.params();
            return { previewUrl, fileName };
        },
        computation: ({ previewUrl, fileName }): FilePreviewViewState => {
            if (!previewUrl) {
                return {
                    contentLoadStatus: FILE_PREVIEW_CONTENT_STATUS.Error,
                    errorDetails: this.createUnavailableError(fileName),
                };
            }

            return {
                contentLoadStatus: FILE_PREVIEW_CONTENT_STATUS.Loading,
                errorDetails: null,
            };
        },
    });
    public readonly errorDetails = computed(
        () => this.viewerState().errorDetails
    );
    public readonly contentLoadStatus = computed(
        () => this.viewerState().contentLoadStatus
    );
    public readonly previewStatus = computed((): PreviewStatus => {
        switch (this.contentLoadStatus()) {
            case FILE_PREVIEW_CONTENT_STATUS.Error:
                return 'error';
            case FILE_PREVIEW_CONTENT_STATUS.Success:
                return 'ready';
            default:
                return 'loading';
        }
    });
    public readonly fileType = computed(
        (): FilePreviewType => this.resolveFileType(this.params().fileName)
    );
    public readonly errorHeading = computed(() =>
        this.params().previewUrl
            ? "Couldn't load document"
            : 'Preview unavailable'
    );
    public readonly previewHeight = '100%';
    //#endregion

    //#region Public methods
    public setParams(params: FilePreviewStateInput): void {
        this.params.set(params);
    }

    public setViewerError(event: Error | ErrorEvent | Event): void {
        const { fileName } = this.params();
        const error = this.clientError.create(
            FILE_PREVIEW_ERROR.ViewerLoadFailed,
            {
                urnParts: [...this.loggingId, 'viewer-load'],
                context: {
                    fileName,
                    fileType: this.fileType(),
                    eventName: this.resolveEventName(event),
                },
                consoleErrorRestArgs: [event],
            }
        );

        this.viewerState.set({
            contentLoadStatus: FILE_PREVIEW_CONTENT_STATUS.Error,
            errorDetails: {
                ...normalizeToProblemDetails(error),
                detail: this.extractErrorMessage(event),
            },
        });
    }

    public setViewerLoaded(): void {
        this.viewerState.set({
            contentLoadStatus: FILE_PREVIEW_CONTENT_STATUS.Success,
            errorDetails: null,
        });
    }
    //#endregion

    //#region Private methods
    private resolveFileType(fileName: string): FilePreviewType {
        const ext = extractFileExtension(fileName.toLowerCase());

        if (!ext) return FILE_PREVIEW_TYPE.Doc;
        if (ext === 'pdf') return FILE_PREVIEW_TYPE.Pdf;
        if (
            isSupportedDocumentFileType(ext) &&
            this.appConfig.imagePreviewFileTypes.includes(ext)
        ) {
            return FILE_PREVIEW_TYPE.Image;
        }

        return FILE_PREVIEW_TYPE.Doc;
    }

    private createUnavailableError(fileName: string): ResolvedProblemDetails {
        const error = this.clientError.create(
            FILE_PREVIEW_ERROR.UrlUnavailable,
            {
                urnParts: [...this.loggingId, 'preview-url'],
                context: {
                    fileName,
                },
            }
        );

        return {
            ...normalizeToProblemDetails(error),
            detail: 'A preview URL is not available for this document yet.',
        };
    }

    private resolveEventName(event: Error | ErrorEvent | Event): string {
        if (event instanceof ErrorEvent) return event.message || event.type;
        if (event instanceof Error) return event.name || 'Error';
        return event.type || 'Event';
    }

    private extractErrorMessage(event: Error | ErrorEvent | Event): string {
        if (event instanceof ErrorEvent) return event.message;

        if (event instanceof Error) {
            if (
                (event as { status?: number }).status ===
                    FILE_PREVIEW_ERROR.ViewerLoadFailed.status &&
                event.name &&
                event.name !== 'ClientError'
            ) {
                return event.name;
            }

            switch (event.name) {
                case 'PasswordException':
                    return 'This PDF is password protected.';
                case 'InvalidPDFException':
                    return 'The PDF file is invalid or corrupted.';
                case 'ResponseException': {
                    const status = (event as { status?: number }).status;

                    switch (status) {
                        case 400:
                            return 'The PDF request was invalid. Please try again.';
                        case 401:
                            return 'You are not authorized to access this PDF.';
                        case 403:
                            return 'You do not have permission to access this PDF.';
                        case 404:
                            return 'The PDF file was not found.';
                        case 500:
                            return 'The server encountered an error. Please try again later.';
                        case 503:
                            return 'The service is temporarily unavailable. Please try again later.';
                        default:
                            return 'Unable to load the PDF. Please try again.';
                    }
                }
                case 'AbortException':
                    return 'PDF loading was cancelled.';
                case 'UnknownErrorException':
                    return 'An unexpected error occurred while loading the PDF.';
                default:
                    return event.message || 'An unexpected error occurred.';
            }
        }

        return 'An unexpected error occurred while loading the document.';
    }
    //#endregion
}
